import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { beginLogin, completeLogin, consumeReturnTo } from '@/lib/auth/oauth';
import { useAuthStore } from '../stores/auth.store';
import { hydrateUserProfile } from '../hooks/useUserProfile';
import { FullScreenLoader } from '@/components/feedback/FullScreenLoader';

/**
 * OAuth redirect target. Exchanges the `code` (with the PKCE verifier) for
 * tokens, stores them, loads the user profile, and continues to the
 * originally-requested page.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    completeLogin()
      .then(async (tokens) => {
        if (!active) return;
        setTokens(tokens);
        // Load the profile (name/email shown in the sidebar) BEFORE entering
        // the app: the one-shot session bootstrap ran before these tokens
        // existed, so nothing else would fetch it until a full page reload.
        // Best-effort — a failure here still lets the login through.
        await hydrateUserProfile();
        if (!active) return;
        navigate(consumeReturnTo(), { replace: true });
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Autentificarea a eșuat.');
      });
    return () => {
      active = false;
    };
  }, [navigate, setTokens]);

  if (error) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="max-w-sm text-sm text-danger">{error}</p>
        <button
          type="button"
          onClick={() => void beginLogin()}
          className="rounded-full bg-accent px-5 py-2.5 text-sm font-bold text-white transition hover:brightness-95"
        >
          Încearcă din nou
        </button>
      </div>
    );
  }

  return <FullScreenLoader />;
}
